import React, { useCallback, useEffect, useState } from 'react';
import { avatarColor, initialOf } from '../../common/avatar';
import {
  CompanyWidgetDuty,
  CompanyWidgetDutyPerson,
  getCompanyWidgetDuty,
} from '../companyHelpers';
import style from './WidgetDutyBoard.module.css';

/* §FB170 Р3: «Хто на чергуванні» у картці компанії — інструмент розслідування скарг пілоту
   («я був у кабінеті, а подзвонив телефон»). Показує кожну відповідальну особу компанії: чи вона
   чергує в кабінеті викликів, у якому стані, чи живий зв'язок і куди пішов її останній виклик.
   Лише читання; оновлюється сам, поки вкладку видно. */

const REFRESH_MS = 15000;

const PAUSE_TEXT: Record<string, string> = {
  manual: 'поставлено вручну',
  missed: 'пропущено виклик',
  no_ack: 'кабінет не отримав виклик',
  no_link: "немає зв'язку",
};

type Tone = 'ok' | 'live' | 'warn' | 'bad' | 'muted';

export const dutyStatus = (
  p: CompanyWidgetDutyPerson,
): { label: string; tone: Tone } => {
  if (p.presence === 'none')
    return { label: 'Не в кабінеті — виклики на телефон', tone: 'muted' };
  if (p.presence === 'cabinet')
    return {
      label: 'У кабінеті, не чергує — виклики на телефон',
      tone: 'muted',
    };
  if (p.stale)
    return {
      label: "Немає зв'язку з кабінетом — виклики на телефон",
      tone: 'bad',
    };
  if (p.suspect)
    return {
      label: 'Кабінет не отримав останній виклик — поки виклики на телефон',
      tone: 'warn',
    };
  switch (p.state) {
    case 'ringing':
      return { label: 'Дзвонить у кабінеті', tone: 'live' };
    case 'in_call':
      return { label: 'У розмові', tone: 'live' };
    case 'paused':
      return {
        label: `Пауза — ${PAUSE_TEXT[p.pausedReason ?? ''] ?? PAUSE_TEXT.manual}`,
        tone: 'warn',
      };
    default:
      return { label: 'На чергуванні — чекає на виклик', tone: 'ok' };
  }
};

/** Підсумок останнього виклику тими самими безособовими формами, що й колонка журналу. */
export const lastCallText = (
  outcome?: string | null,
  channel?: string | null,
): string => {
  switch (outcome) {
    case 'answered':
      return channel === 'web' ? 'прийнято в кабінеті' : 'прийнято на телефоні';
    case 'declined':
      return 'відхилено';
    case 'missed':
      return 'пропущено';
    case 'caller_canceled':
      return 'абонент скасував';
    case 'no_responsible':
      return 'без відповідального';
    case 'not_connected':
      return "не з'єдналося";
    case 'ringing':
      return channel === 'web' ? 'дзвонить у кабінеті' : 'дзвонить на телефоні';
    default:
      return '—';
  }
};

const kyiv = (iso?: string | null, withDate = false): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleString('uk-UA', {
      timeZone: 'Europe/Kyiv',
      ...(withDate ? { day: '2-digit', month: '2-digit' } : {}),
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d.toLocaleString('uk-UA');
  }
};

const agoText = (fromIso?: string | null, nowIso?: string): string => {
  if (!fromIso || !nowIso) return '';
  const s = Math.max(
    0,
    Math.round(
      (new Date(nowIso).getTime() - new Date(fromIso).getTime()) / 1000,
    ),
  );
  if (s < 60) return `${s} с тому`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m} хв тому` : `${Math.round(m / 60)} год тому`;
};

/** Чого бракує вкладці — лише проблеми, щоб рядок не тонув у галочках. */
const readinessIssues = (p: CompanyWidgetDutyPerson): string[] => {
  const out: string[] = [];
  if (!p.wsOnline) out.push("немає з'єднання з відеосервісом");
  if (!p.audible) out.push('без звуку');
  if (!p.mic) out.push('без мікрофона');
  if (!p.notif) out.push('сповіщення вимкнено');
  if (!p.visible) out.push('вкладка у фоні');
  return out;
};

const WidgetDutyBoard: React.FC<{ companyId: number | string }> = ({
  companyId,
}) => {
  const [board, setBoard] = useState<CompanyWidgetDuty | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBoard(await getCompanyWidgetDuty(companyId));
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load]);

  const onDuty = board?.people.filter((p) => p.presence === 'duty').length ?? 0;

  return (
    <div className={style.section} aria-live="polite">
      <div className={style.head}>
        <p className={style.title}>Хто на чергуванні</p>
        {board && <span className={style.count}>{onDuty}</span>}
        <button
          type="button"
          className={style.refresh}
          onClick={() => void load()}
          disabled={loading}
          title="Оновити"
          aria-label="Оновити список чергувань"
        >
          ⟳
        </button>
      </div>
      {failed && !board && (
        <p className={style.muted}>Не вдалося завантажити. Спробуйте ще раз.</p>
      )}
      {board && !board.globalEnabled && (
        <p className={style.note}>
          Кабінет викликів ще не ввімкнено в системі — усі виклики з віджета
          йдуть на телефон.
        </p>
      )}
      {board && board.people.length === 0 && (
        <p className={style.muted}>
          У компанії немає відповідальних осіб із роллю «Дзвінки з QR».
        </p>
      )}
      {board && board.people.length > 0 && (
        <ul className={style.list}>
          {board.people.map((p) => {
            const st = dutyStatus(p);
            const issues = p.presence === 'duty' ? readinessIssues(p) : [];
            return (
              <li key={p.userId} className={style.row}>
                {p.avatarUrl ? (
                  <img className={style.avatar} src={p.avatarUrl} alt="" />
                ) : (
                  <span
                    className={style.avatar}
                    style={{ backgroundColor: avatarColor(p.userName) }}
                    aria-hidden="true"
                  >
                    {initialOf(p.userName)}
                  </span>
                )}
                <div className={style.main}>
                  <div className={style.nameLine}>
                    <b>{p.userName}</b>
                    {!p.isResponsible && (
                      <span className={style.muted}>роль знято</span>
                    )}
                  </div>
                  <span className={`${style.badge} ${style[st.tone]}`}>
                    {st.label}
                  </span>
                  {p.presence === 'duty' && (
                    <div className={style.meta}>
                      {p.dutyStartedAtUtc && (
                        <span>на чергуванні з {kyiv(p.dutyStartedAtUtc)}</span>
                      )}
                      {p.lastHeartbeatAtUtc && (
                        <span className={p.stale ? style.bad : undefined}>
                          зв'язок:{' '}
                          {agoText(p.lastHeartbeatAtUtc, board.serverNowUtc)}
                        </span>
                      )}
                      {issues.length > 0 && (
                        <span className={style.warnText}>
                          {issues.join(', ')}
                        </span>
                      )}
                    </div>
                  )}
                  {p.presence === 'cabinet' && p.cabinetSeenAtUtc && (
                    <div className={style.meta}>
                      <span>
                        остання дія в кабінеті:{' '}
                        {agoText(p.cabinetSeenAtUtc, board.serverNowUtc)}
                      </span>
                    </div>
                  )}
                </div>
                <div className={style.last}>
                  <span className={style.lastLabel}>Останній виклик</span>
                  {p.lastCallAtUtc ? (
                    <>
                      <span>{kyiv(p.lastCallAtUtc, true)}</span>
                      <span className={style.lastOutcome}>
                        {lastCallText(p.lastCallOutcome, p.lastCallChannel)}
                      </span>
                    </>
                  ) : (
                    <span className={style.muted}>ще не було</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default WidgetDutyBoard;
